import { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle, Eye, EyeOff, Clock, Linkedin, Briefcase, Github, GitBranch, MessageSquare, Users, AlertTriangle } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { useToast } from '@shared/hooks/useToast';
import {
  sourceConnectorsService,
  type SourceConnectorKind,
  type SourceConnectorRow,
  type SourceConnectorAuthKind,
} from '@core/supabase/services/sourceConnectorsService';

function Section({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="ggj-param-group overflow-hidden">
      <div className="ggj-profile-section-h flex items-center gap-2">{title}</div>
      <div className="ggj-profile-section-body">{children}</div>
    </div>
  );
}
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { supabase } from '@core/supabase/client';
import { scheduledJobService, type ScheduledJobRow } from '@core/supabase/services/scheduledJobService';
import { PermissionGate } from '@shared/components/ui/PermissionGate';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

type Provider = 'smtp' | 'slack' | 'google_calendar' | 'linkedin' | 'groq';

interface Integration {
  id: string;
  provider: Provider;
  settings: Record<string, unknown>;
  is_active: boolean;
  updated_at: string;
}

interface ProviderDef { key: Provider; label: string; description: string }

const PROVIDER_GROUPS: { group: string; providers: ProviderDef[] }[] = [
  {
    group: 'Communication',
    providers: [
      { key: 'smtp',  label: 'SMTP / Email sender', description: 'Custom outbound email domain (e.g. notifications@yourdomain.com)' },
      { key: 'slack', label: 'Slack',                description: 'Post new application or interview notifications to a Slack channel.' },
    ],
  },
  {
    group: 'Calendar & Scheduling',
    providers: [
      { key: 'google_calendar', label: 'Google Calendar', description: 'Sync interview events to a shared Google Calendar.' },
    ],
  },
  {
    group: 'Job Boards',
    providers: [
      { key: 'linkedin', label: 'LinkedIn Jobs', description: 'Authenticate for one-click job posting to LinkedIn.' },
    ],
  },
  {
    group: 'AI & Intelligence',
    providers: [
      { key: 'groq', label: 'Groq (AI / Agent)', description: 'Optional org API key for Llama chat (Agent, requisitions AI). If unset, the server default key is used when configured.' },
    ],
  },
];


function SmtpForm({
  initial,
  onSave,
  saving,
  defaultTestEmail,
}: {
  initial: Record<string, unknown>;
  onSave: (settings: Record<string, unknown>) => Promise<void>;
  saving: boolean;
  defaultTestEmail?: string | null;
}) {
  const [form, setForm] = useState({
    host: (initial.host as string) ?? '',
    port: String(initial.port ?? '587'),
    user: (initial.user as string) ?? '',
    password: (initial.password as string) ?? '',
    from_address: (initial.from_address as string) ?? '',
    from_name: (initial.from_name as string) ?? '',
    use_tls: initial.use_tls !== false,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testHint, setTestHint] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const runSmtpTest = async () => {
    setTestHint(null);
    if (!form.host.trim() || !form.user.trim()) {
      setTestHint('Host and username are required to test.');
      return;
    }
    setTesting(true);
    try {
      const port = parseInt(form.port) || 587;
      const { data, error } = await supabase.functions.invoke('ggj_b2b_test-smtp', {
        body: {
          host: form.host.trim(),
          port,
          user: form.user.trim(),
          password: form.password,
          use_tls: form.use_tls,
          secure: port === 465,
          from_address: form.from_address.trim() || undefined,
          from_name: form.from_name.trim() || undefined,
          test_to: defaultTestEmail?.trim() || undefined,
        },
      });
      if (error) throw new Error(error.message);
      const d = data as { ok?: boolean; error?: string; sent_test?: boolean };
      if (d?.error) throw new Error(d.error);
      setTestHint(
        d?.sent_test
          ? 'Connection OK — test message sent to your work email.'
          : 'SMTP connection verified successfully.'
      );
    } catch (e) {
      setTestHint(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">SMTP host</label>
          <input className="input" placeholder="smtp.sendgrid.net" value={form.host} onChange={set('host')} />
        </div>
        <div>
          <label className="label">Port</label>
          <input className="input" type="number" placeholder="587" value={form.port} onChange={set('port')} />
        </div>
      </div>
      <div>
        <label className="label">Username / API key</label>
        <input className="input" placeholder="apikey" value={form.user} onChange={set('user')} />
      </div>
      <div>
        <label className="label">Password / secret</label>
        <div className="relative">
          <input
            className="input pr-9"
            type={showPwd ? 'text' : 'password'}
            placeholder="••••••••"
            value={form.password}
            onChange={set('password')}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 vscode-text-secondary hover:vscode-text-primary"
            onClick={() => setShowPwd((v) => !v)}
          >
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">From address</label>
          <input className="input" placeholder="noreply@yourdomain.com" value={form.from_address} onChange={set('from_address')} />
        </div>
        <div>
          <label className="label">From name</label>
          <input className="input" placeholder="GoGrabJob" value={form.from_name} onChange={set('from_name')} />
        </div>
      </div>
      <label className="flex items-center gap-2 ggj-text-body vscode-text-secondary cursor-pointer">
        <input
          type="checkbox"
          className="rounded border-[var(--vscode-border-primary)]"
          checked={form.use_tls}
          onChange={(e) => setForm((f) => ({ ...f, use_tls: e.target.checked }))}
        />
        Use TLS / STARTTLS
      </label>
      {testHint ? <p className="ggj-text-meta vscode-text-secondary">{testHint}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={testing || saving}
          onClick={() => void runSmtpTest()}
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : '[Test Connection]'}
        </button>
        <button
          className="btn-primary"
          disabled={saving}
          onClick={() =>
            onSave({
              host: form.host,
              port: parseInt(form.port) || 587,
              user: form.user,
              password: form.password,
              from_address: form.from_address,
              from_name: form.from_name,
              use_tls: form.use_tls,
            })
          }
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save SMTP settings'}
        </button>
      </div>
    </div>
  );
}

function ApiKeyForm({
  label,
  initial,
  onSave,
  saving,
}: {
  label: string;
  initial: Record<string, unknown>;
  onSave: (settings: Record<string, unknown>) => Promise<void>;
  saving: boolean;
}) {
  const [apiKey, setApiKey] = useState((initial.api_key as string) ?? '');
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-3">
      <div>
        <label className="label">{label} API key</label>
        <div className="relative">
          <input
            className="input pr-9"
            type={show ? 'text' : 'password'}
            placeholder="••••••••"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 vscode-text-secondary hover:vscode-text-primary"
            onClick={() => setShow((v) => !v)}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <button
        className="btn-primary"
        disabled={saving || !apiKey}
        onClick={() => onSave({ api_key: apiKey })}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save ${label} key`}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Phase 8 · Cluster C-43 — Sourcing connectors section
//
// Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
// Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
//
// Six rows: linkedin, naukri, github, gitlab, discord, internal. Each
// row reflects the current row in `ggj_b2b_source_connectors` (status
// pill + last used) with Connect / Reconnect / Disconnect CTAs behind
// the admin gate already enforced by RLS. Internal is always active and
// has no CTA. The actual OAuth handshake for LinkedIn is stubbed per
// C-43 DON'T — we offer a paste-cookie fallback for local dev.
// ═══════════════════════════════════════════════════════════════

type SourcingKindUI = SourceConnectorKind;

interface SourcingDef {
  kind: SourcingKindUI;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Kinds of auth the admin can pick in the Connect modal. */
  authOptions: Array<{
    kind: SourceConnectorAuthKind;
    label: string;
    help?: string;
    oauthStub?: boolean;
    /** Show a secondary "Guild ID" input and persist it to `config_json.guildId`. Discord-only today. */
    requiresGuildId?: boolean;
  }>;
}

const SOURCING_DEFS: SourcingDef[] = [
  {
    kind: 'linkedin',
    label: 'LinkedIn',
    description: 'Sales Navigator / Recruiter people search via OAuth or a self-hosted proxy.',
    Icon: Linkedin,
    authOptions: [
      { kind: 'oauth',          label: 'Sign in with LinkedIn',  help: 'Opens the LinkedIn OAuth consent screen.', oauthStub: true },
      { kind: 'session_cookie', label: 'Paste li_at cookie', help: 'Open linkedin.com → DevTools → Application → Cookies → copy the li_at value. ⚠️ A self-hosted linkedin-api proxy must be running at VITE_LINKEDIN_PROXY_URL for this to work — the browser cannot call LinkedIn directly.' },
    ],
  },
  {
    kind: 'naukri',
    label: 'Naukri',
    description: 'Candidate search via Naukri. Use an RMS API key (paid) or your personal session cookie (nauk_at) for dev/personal accounts.',
    Icon: Briefcase,
    authOptions: [
      {
        kind: 'session_cookie',
        label: 'Paste nauk_at cookie',
        help: 'Open naukri.com → DevTools → Application → Cookies → copy the nauk_at value. Works with any personal or recruiter login — no RMS subscription needed.',
      },
      {
        kind: 'api_key',
        label: 'Paste RMS API key',
        help: 'Available from your Naukri RMS recruiter dashboard under Account → API Integration. Requires a paid Naukri RMS subscription.',
      },
    ],
  },
  {
    kind: 'github',
    label: 'GitHub',
    description: 'Discover open-source contributors by language, stars, and location. Token optional (lifts rate ceiling).',
    Icon: Github,
    authOptions: [
      { kind: 'api_key', label: 'Paste personal access token', help: 'A classic PAT with `read:user` scope. Optional — public search works without it, at 30 requests/min.' },
    ],
  },
  {
    kind: 'gitlab',
    label: 'GitLab',
    description: 'People search on GitLab.com. Token optional (lifts rate ceiling).',
    Icon: GitBranch,
    authOptions: [
      { kind: 'api_key', label: 'Paste personal access token', help: 'A GitLab PAT with `read_user` scope. Optional.' },
    ],
  },
  {
    kind: 'discord',
    label: 'Discord',
    description: 'Surface members of a sourcing guild via a read-only bot token.',
    Icon: MessageSquare,
    authOptions: [
      {
        kind: 'bot_token',
        label: 'Paste bot token + Guild ID',
        help: 'Requires the bot to be a member of the target server with the Server Members Intent enabled.',
        requiresGuildId: true,
      },
    ],
  },
  {
    kind: 'internal',
    label: 'Internal pool',
    description: "Your own candidates table. Always on — no credentials required.",
    Icon: Users,
    authOptions: [],
  },
];

function connectorStatusLabel(row: SourceConnectorRow | null, kind: SourcingKindUI): { label: string; tone: 'ok' | 'off' | 'warn' } {
  if (kind === 'internal') return { label: 'Active', tone: 'ok' };
  if (!row) return { label: 'Not configured', tone: 'off' };
  if (row.status === 'active') return { label: 'Active', tone: 'ok' };
  if (row.status === 'expired') return { label: 'Expired', tone: 'warn' };
  return { label: 'Revoked', tone: 'warn' };
}

interface ConnectModalProps {
  open: boolean;
  def: SourcingDef | null;
  saving: boolean;
  onClose: () => void;
  onSave: (
    kind: SourceConnectorKind,
    payload: { authKind: SourceConnectorAuthKind; secret: string; config?: Record<string, unknown> },
  ) => Promise<void>;
  onOauthStub: (kind: SourceConnectorKind) => void;
}

function ConnectModal({ open, def, saving, onClose, onSave, onOauthStub }: ConnectModalProps) {
  const [authKind, setAuthKind] = useState<SourceConnectorAuthKind>('api_key');
  const [secret, setSecret] = useState('');
  const [show, setShow] = useState(false);
  const [guildId, setGuildId] = useState('');

  useEffect(() => {
    if (!def) return;
    // Default to the first non-OAuth option so the form has an input
    // visible by default; OAuth is opt-in via its own button.
    const firstInput = def.authOptions.find((o) => !o.oauthStub);
    setAuthKind(firstInput?.kind ?? def.authOptions[0]?.kind ?? 'api_key');
    setSecret('');
    setGuildId('');
    setShow(false);
  }, [def]);

  if (!def) return null;

  const currentOpt = def.authOptions.find((o) => o.kind === authKind);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Connect ${def.label}`}
      subtitle={def.description}
      icon={<def.Icon className="w-4 h-4" />}
      size="md"
    >
      <div className="p-5 space-y-4">
        {def.authOptions.length > 1 && (
          <div>
            <label className="label mb-1 block">Authentication</label>
            <div className="flex flex-wrap gap-2">
              {def.authOptions.map((opt) => (
                <button
                  key={opt.kind}
                  type="button"
                  className={[
                    'px-2.5 py-1 rounded-[8px] text-[12px] border transition-colors',
                    authKind === opt.kind
                      ? 'bg-[color-mix(in_srgb,#8B80F9_15%,transparent)] border-[#8B80F9] text-[#8B80F9]'
                      : 'vscode-border vscode-text-secondary hover:vscode-text-primary',
                  ].join(' ')}
                  onClick={() => setAuthKind(opt.kind)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentOpt?.oauthStub ? (
          <div className="rounded-[10px] border vscode-border p-3 space-y-2">
            <div className="flex items-center gap-2 ggj-text-meta vscode-text-secondary">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              OAuth redirect is gated behind admin approval.
            </div>
            <p className="ggj-text-meta vscode-text-tertiary">
              The full LinkedIn OAuth handshake lands in a follow-up PR. For now, switch to
              "Paste session cookie" to unblock local development.
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onOauthStub(def.kind)}
            >
              Request OAuth access
            </button>
          </div>
        ) : (
          <div>
            <label className="label">{currentOpt?.label ?? 'Secret'}</label>
            <div className="relative">
              <input
                className="input pr-9"
                type={show ? 'text' : 'password'}
                placeholder="••••••••••••"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 vscode-text-secondary hover:vscode-text-primary"
                onClick={() => setShow((v) => !v)}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {currentOpt?.help && (
              <p className="ggj-text-meta vscode-text-tertiary mt-1.5">{currentOpt.help}</p>
            )}
            {currentOpt?.requiresGuildId && (
              <div className="mt-3">
                <label className="label">Guild ID</label>
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. 987654321098765432"
                  value={guildId}
                  onChange={(e) => setGuildId(e.target.value)}
                />
                <p className="ggj-text-meta vscode-text-tertiary mt-1.5">
                  Enable Developer Mode in Discord, right-click the server, and choose "Copy Server ID".
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {!currentOpt?.oauthStub && (
            <button
              className="btn-primary"
              disabled={
                saving ||
                !secret.trim() ||
                (currentOpt?.requiresGuildId === true && !guildId.trim())
              }
              onClick={() =>
                void onSave(def.kind, {
                  authKind,
                  secret: secret.trim(),
                  config: currentOpt?.requiresGuildId ? { guildId: guildId.trim() } : undefined,
                })
              }
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & connect'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

interface SourcingConnectorsSectionProps {
  orgId: string;
  canManage: boolean;
}

function SourcingConnectorsSection({ orgId, canManage }: SourcingConnectorsSectionProps) {
  const [rows, setRows] = useState<Partial<Record<SourceConnectorKind, SourceConnectorRow>>>({});
  const [loading, setLoading] = useState(true);
  const [modalDef, setModalDef] = useState<SourcingDef | null>(null);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  const refresh = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    sourceConnectorsService
      .list(orgId)
      .then((list) => {
        const map: Partial<Record<SourceConnectorKind, SourceConnectorRow>> = {};
        for (const r of list) map[r.kind] = r;
        setRows(map);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = async (
    kind: SourceConnectorKind,
    payload: { authKind: SourceConnectorAuthKind; secret: string; config?: Record<string, unknown> },
  ) => {
    setSaving(true);
    try {
      await sourceConnectorsService.upsert(orgId, kind, {
        authKind: payload.authKind,
        secret: payload.secret,
        status: 'active',
        config: payload.config,
      });
      showToast({ title: `${kind} connected`, variant: 'success' });
      setModalDef(null);
      refresh();
    } catch (err) {
      showToast({
        title: 'Connect failed',
        body: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (kind: SourceConnectorKind) => {
    try {
      await sourceConnectorsService.setStatus(orgId, kind, 'revoked');
      showToast({ title: `${kind} disconnected`, variant: 'info' });
      refresh();
    } catch (err) {
      showToast({
        title: 'Disconnect failed',
        body: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  };

  const handleOauthStub = (_kind: SourceConnectorKind) => {
    showToast({
      title: 'Coming after admin approval',
      body: 'LinkedIn OAuth is gated for production. Use the session cookie path for local dev.',
      variant: 'info',
    });
  };

  return (
    <Section title={<><Users className="w-3.5 h-3.5" /> Sourcing connectors</>}>
      <div className="space-y-1">
        <p className="ggj-text-meta vscode-text-tertiary leading-relaxed mb-2">
          Credentials the agent uses when it runs <span className="font-mono vscode-text-secondary">source.*</span> tools.
          Secrets are never surfaced to the browser — this list only shows auth kind + status.
        </p>
        {loading ? (
          <div className="py-6 flex items-center gap-2 vscode-text-tertiary">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="-mx-4 px-4">
            {SOURCING_DEFS.map((def) => {
              const existing = rows[def.kind] ?? null;
              const pill = connectorStatusLabel(existing, def.kind);
              const isInternal = def.kind === 'internal';
              return (
                <div
                  key={def.kind}
                  className="flex items-center gap-3 border-b border-[var(--vscode-border-primary)]/40 last:border-0 py-3"
                >
                  <div className="w-8 h-8 rounded-[8px] flex items-center justify-center vscode-bg-tertiary shrink-0">
                    <def.Icon className="w-4 h-4 vscode-text-secondary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="ggj-text-body font-medium vscode-text-primary">{def.label}</p>
                      <span
                        className={[
                          'inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full',
                          pill.tone === 'ok'
                            ? 'bg-green-500/10 text-green-400'
                            : pill.tone === 'warn'
                            ? 'bg-amber-500/10 text-amber-400'
                            : 'vscode-bg-tertiary vscode-text-tertiary',
                        ].join(' ')}
                      >
                        {pill.tone === 'ok' && <CheckCircle className="w-3 h-3" />}
                        {pill.tone === 'warn' && <AlertTriangle className="w-3 h-3" />}
                        {pill.label}
                      </span>
                      {existing?.lastUsedAt && (
                        <span className="ggj-text-meta vscode-text-tertiary">
                          · last used {new Date(existing.lastUsedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="ggj-text-meta vscode-text-tertiary mt-0.5">{def.description}</p>
                  </div>
                  {!isInternal && canManage && (
                    <div className="flex items-center gap-2 shrink-0">
                      {existing && existing.status === 'active' ? (
                        <>
                          <button
                            type="button"
                            className="ggj-text-meta vscode-text-secondary hover:vscode-text-primary"
                            onClick={() => setModalDef(def)}
                          >
                            Reconnect
                          </button>
                          <button
                            type="button"
                            className="ggj-text-meta text-red-400 hover:text-red-300"
                            onClick={() => void handleDisconnect(def.kind)}
                          >
                            Disconnect
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="ggj-text-meta vscode-text-secondary hover:vscode-text-primary"
                          onClick={() => setModalDef(def)}
                        >
                          {existing ? 'Reconnect' : 'Connect'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConnectModal
        open={modalDef !== null}
        def={modalDef}
        saving={saving}
        onClose={() => setModalDef(null)}
        onSave={handleSave}
        onOauthStub={handleOauthStub}
      />
      {toast}
    </Section>
  );
}

export function IntegrationsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const [integrations, setIntegrations] = useState<Partial<Record<Provider, Integration>>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Provider | null>(null);
  const [saving, setSaving] = useState<Provider | null>(null);
  const [saved, setSaved] = useState<Provider | null>(null);
  const [scheduledJobs, setScheduledJobs] = useState<ScheduledJobRow[]>([]);
  const [pendingEmailQueue, setPendingEmailQueue] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.orgId) return;
    supabase
      .from('ggj_b2b_org_integrations')
      .select('*')
      .eq('org_id', user.orgId)
      .then(({ data }) => {
        const map: Partial<Record<Provider, Integration>> = {};
        (data ?? []).forEach((row) => {
          map[row.provider as Provider] = row as Integration;
        });
        setIntegrations(map);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId || !perms.canManageTeam) return;
    void scheduledJobService.listForOrg(user.orgId, { limit: 15 }).then(setScheduledJobs).catch(() => setScheduledJobs([]));
    void supabase
      .from('ggj_b2b_email_send_queue')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', user.orgId)
      .eq('status', 'pending')
      .then(({ count }) => setPendingEmailQueue(count ?? 0))
      .catch(() => setPendingEmailQueue(null));
  }, [user?.orgId, perms.canManageTeam]);

  const handleSave = async (provider: Provider, settings: Record<string, unknown>) => {
    if (!user?.orgId || !perms.canManageTeam) return;
    setSaving(provider);
    try {
      const { data, error } = await supabase
        .from('ggj_b2b_org_integrations')
        .upsert(
          { org_id: user.orgId, provider, settings, is_active: true },
          { onConflict: 'org_id,provider' }
        )
        .select()
        .single();
      if (error) throw error;
      setIntegrations((prev) => ({ ...prev, [provider]: data as Integration }));
      setSaved(provider);
      setTimeout(() => setSaved(null), 3000);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="animate-pulse card h-40" />;

  if (!perms.canManageTeam) {
    return (
      <PermissionGate
        allowed={false}
        label="Integrations — Admin only"
        detail="You need admin or owner access to configure integrations."
      />
    );
  }

  const renderProviderRow = (key: Provider, label: string, description: string) => {
    const existing = integrations[key];
    const isOpen   = expanded === key;
    return (
      <div key={key} className="border-b border-[var(--vscode-border-primary)]/40 last:border-0">
        <div className="flex items-center justify-between py-3 cursor-pointer"
          onClick={() => setExpanded(isOpen ? null : key)}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="ggj-text-body font-medium vscode-text-primary">{label}</p>
              {existing?.is_active ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">
                  <CheckCircle className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full vscode-bg-tertiary vscode-text-tertiary">
                  Not configured
                </span>
              )}
            </div>
            <p className="ggj-text-meta vscode-text-tertiary mt-0.5">{description}</p>
          </div>
          <button type="button" className="ggj-text-meta vscode-text-secondary hover:vscode-text-primary ml-3 shrink-0">
            {isOpen ? 'Close' : existing ? 'Edit' : 'Configure'}
          </button>
        </div>

        {isOpen && (
          <div className="pb-4 border-t border-[var(--vscode-border-primary)]/40 pt-4">
            {saved === key && (
              <div className="mb-3 flex items-center gap-2 ggj-text-body text-green-400">
                <CheckCircle className="w-4 h-4" /> Saved
              </div>
            )}
            {key === 'smtp' ? (
              <SmtpForm initial={(existing?.settings ?? {}) as Record<string, unknown>}
                onSave={(s) => handleSave(key, s)} saving={saving === key} defaultTestEmail={user?.email} />
            ) : (
              <ApiKeyForm label={label} initial={(existing?.settings ?? {}) as Record<string, unknown>}
                onSave={(s) => handleSave(key, s)} saving={saving === key} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Integrations</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Connect external services — SMTP, Slack, Google Calendar, and AI providers.</p>
      </div>

      {/* ── Grouped integration providers ── */}
      {PROVIDER_GROUPS.map(({ group, providers }) => (
        <Section key={group} title={group}>
          <div className="py-0 -mx-4 px-4">
            {providers.map(({ key, label, description }) =>
              renderProviderRow(key, label, description)
            )}
          </div>
        </Section>
      ))}

      {/* ── C-43: Sourcing connectors — anchor id used by deep-links from ── */}
      {/* the Smart Match / Sourcing canvas "Configure" buttons.            ── */}
      <div id="sourcing-connectors">
        {user?.orgId && (
          <SourcingConnectorsSection orgId={user.orgId} canManage={perms.canManageTeam} />
        )}
      </div>

      {/* ── Background schedulers ── */}
      <Section title={<><Clock className="w-3.5 h-3.5" /> Background Schedulers</>}>
        <div className="space-y-3">
          <p className="ggj-text-meta vscode-text-tertiary leading-relaxed">
            <span className="font-mono vscode-text-secondary">ggj_b2b_scheduled_jobs</span> holds timed work (job_publish, req_reminder, retention_purge, sequence_send).
            The edge function also drains <span className="font-mono vscode-text-secondary">ggj_b2b_email_send_queue</span> via <span className="font-mono vscode-text-secondary">RESEND_API_KEY</span>.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-[10px] border vscode-border p-3">
              <p className="ggj-text-meta vscode-text-tertiary mb-1">Pending email queue (this org)</p>
              <p className="ggj-text-lead font-semibold vscode-text-primary">{pendingEmailQueue === null ? '—' : pendingEmailQueue}</p>
            </div>
            <div className="rounded-[10px] border vscode-border p-3">
              <p className="ggj-text-meta vscode-text-tertiary mb-1">Recent scheduled jobs (latest 15)</p>
              {scheduledJobs.length === 0 ? (
                <p className="ggj-text-meta vscode-text-tertiary">None</p>
              ) : (
                <ul className="space-y-1.5 max-h-40 overflow-y-auto vscode-text-secondary font-mono text-[11px]">
                  {scheduledJobs.map((j) => (
                    <li key={j.id} className="flex flex-wrap gap-x-2 border-b border-[var(--vscode-border-primary)]/60 pb-1">
                      <span className="text-brand-400/90">{j.job_type}</span>
                      <span>{j.status}</span>
                      <span className="vscode-text-tertiary">{formatDateTimeGmt(j.run_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Marketplace roadmap ── */}
      <Section title="Marketplace Roadmap">
        <div className="space-y-2">
          <p className="ggj-text-meta vscode-text-tertiary mb-2">
            More connectors planned behind org plans and security review.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {['Zoom / Google Meet automation', 'Microsoft Teams', 'Outbound webhooks', 'Indeed / Naukri apply'].map((lbl) => (
              <div key={lbl} className="rounded-[10px] border border-dashed vscode-border px-3 py-2.5 opacity-70">
                <p className="ggj-text-body vscode-text-secondary">{lbl}</p>
                <p className="text-[10px] vscode-text-tertiary mt-0.5">Planned</p>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
